import {
  Button,
  Col,
  Layout,
  Row,
  Tabs,
  DatePicker,
  Table,
  Dropdown,
  Menu,
  message,
} from "antd";
import {
  Eye,
  Pen,
  ChevronDown,
  FileText,
} from "lucide-react";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../../Components/Navbar/Navbar";
import PageHeader from "../../../Components/PageHeader/PageHeader";
import {
  formatDate,
  formatNumbers,
  getDateRange,
  navigateTo,
  getUserRole,
} from "../../../Utils/Common";
import { hasRole } from "../../../Utils/AccessControl";
import PersistentFooter from "../../../Components/PersistentFooter/PersistentFooter";
import dayjs from "dayjs";
import { useTableData } from "../../../Utils/useTableData";
import { getRequest, postRequest } from "../../../Utils/ApiCalls";
import { downloadPettyCashVoucherPdf } from "../../../Utils/pettyCashVoucherPdf";

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const PettyCashRequisition = () => {
  const dateRange = getDateRange("week");
  const [activeTab, setActiveTab] = useState("pending");
  const [downloadingId, setDownloadingId] = useState<string | number | null>(
    null
  );

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    handleChangePage,
    handlePageSizeChange,
  } = useTableData<any>({
    endpoint: "petty_cash_requisitions/get",
    initialFilters: {
      date_from: formatDate(dateRange[0], "YYYY-MM-DD"),
      date_to: formatDate(dateRange[1], "YYYY-MM-DD"),
      status: "pending",
      page_size: 10,
      search: "",
    },
    isFormData: true,
  });

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({
      ...prev,
      page: 1,
      status: activeKey,
    }));
  }

  async function createPettyCashVoucher(record: any) {
    setDownloadingId(record.id);
    try {
      const response = await getRequest(
        "petty_cash_requisitions/get",
        { petty_cash_requisition_id: record.id },
        true
      );
      const detail = response.success
        ? response.data?.data?.[0] ?? record
        : record;

      if (!response.success) {
        message.warning("Could not load full requisition details.");
      }

      await downloadPettyCashVoucherPdf(detail);

      const statusResponse = await postRequest(
        "petty_cash_requisitions/change_status",
        { petty_cash_requisition_id: record.id, action: "completed" },
        true
      );

      if (statusResponse.success) {
        message.success("Petty cash voucher downloaded.");
        setFilterConfig((prev: any) => ({ ...prev }));
      } else {
        message.warning(
          "Voucher downloaded, but failed to mark requisition as completed."
        );
      }
    } catch {
      message.error("Failed to create petty cash voucher.");
    } finally {
      setDownloadingId(null);
    }
  }

  function getTableColumns() {
    const baseColumns = [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        render: (date: any) => formatDate(date, "MMM DD, YYYY"),
      },
      {
        title: "Branch",
        dataIndex: "branch_name",
        key: "branch_name",
      },
      // {
      //   title: "List of Particulars",
      //   key: "items",
      //   render: (_text: any, record: any) =>
      //     record.items?.length ? (
      //       record.items.map((item: any, index: number) => (
      //         <div key={item.id ?? index}>{item.item_name}</div>
      //       ))
      //     ) : (
      //       <span>-</span>
      //     ),
      // },
      {
        title: "Supplier name",
        dataIndex: "supplier_name",
        key: "supplier_name",
      },
      {
        title: "Amount",
        dataIndex: "grand_total",
        key: "grand_total",
        align: "right" as any,
        render: (value: any) => formatNumbers(value),
      },
    ];

    switch (activeTab) {
      case "pending":
        return [
          ...baseColumns,
          {
            title: "Added By",
            dataIndex: "added_by_name",
            key: "added_by_name",
          },
          {
            title: "",
            key: "action",
            width: 10,
            align: "center" as const,
            render: (_text: any, record: any) => {
              const menu = (
                <Menu>
                  {hasRole(getUserRole(), "custodian", "admin") && (
                    <>
                      <Menu.Item
                        key="view"
                        icon={<Eye strokeWidth={1.5} size={15} />}
                        onClick={() =>
                          navigateTo(`/petty-cash-requisition/view/${record.id}`)
                        }
                      >
                        View
                      </Menu.Item>
                      <Menu.Item
                        key="edit"
                        icon={<Pen strokeWidth={1.5} size={15} />}
                        onClick={() =>
                          navigateTo(`/petty-cash-requisition/edit/${record.id}`)
                        }
                      >
                        Edit
                      </Menu.Item>
                    </>
                  )}
                  {hasRole(getUserRole(), "admin", "supervisor") && (
                    <Menu.Item
                      key="approve"
                      icon={<Eye strokeWidth={1.5} size={15} />}
                      onClick={() =>
                        navigateTo(`/petty-cash-requisition/approval/${record.id}`)
                      }
                    >
                      Approve
                    </Menu.Item>
                  )}
                </Menu>
              );
              return (
                <Dropdown overlay={menu} trigger={["click"]}>
                  <Button>
                    Actions <ChevronDown strokeWidth={0.5} />
                  </Button>
                </Dropdown>
              );
            },
          },
        ];
      case "approved":
        return [
          ...baseColumns,
          {
            title: "Approved By",
            dataIndex: "approver_name",
            key: "approver_name",
            render: (value: any, record: any) =>
              value || record.approved_by_name || "-",
          },
          {
            title: "Date Approved",
            dataIndex: "approved_on",
            key: "approved_on",
            render: (value: any) => formatDate(value, "MM/DD/YYYY"),
          },
          {
            title: "",
            key: "action",
            width: 10,
            align: "center" as const,
            render: (_text: any, record: any) => {
              const menu = (
                <Menu>
                  <Menu.Item
                    key="view"
                    icon={<Eye strokeWidth={1.5} size={15} />}
                    onClick={() =>
                      navigateTo(`/petty-cash-requisition/view/${record.id}`)
                    }
                  >
                    View
                  </Menu.Item>
                  {hasRole(getUserRole(), "custodian", "admin") && (
                    <Menu.Item
                      key="create-voucher"
                      icon={<FileText strokeWidth={1.5} size={15} />}
                      disabled={downloadingId === record.id}
                      onClick={() => createPettyCashVoucher(record)}
                    >
                      {downloadingId === record.id
                        ? "Downloading..."
                        : "Create Petty Cash Voucher"}
                    </Menu.Item>
                  )}
                </Menu>
              );
              return (
                <Dropdown overlay={menu} trigger={["click"]}>
                  <Button>
                    Actions <ChevronDown strokeWidth={0.5} />
                  </Button>
                </Dropdown>
              );
            },
          },
        ];
      case "completed":
        return [
          ...baseColumns,
          {
            title: "Approved By",
            dataIndex: "approver_name",
            key: "approver_name",
            render: (value: any, record: any) =>
              value || record.approved_by_name || "-",
          },
          {
            title: "Date Approved",
            dataIndex: "approved_on",
            key: "approved_on",
            render: (value: any) => formatDate(value, "MM/DD/YYYY"),
          },
          {
            title: "",
            key: "action",
            width: 10,
            align: "center" as const,
            render: (_text: any, record: any) => {
              const menu = (
                <Menu>
                  <Menu.Item
                    key="view"
                    icon={<Eye strokeWidth={1.5} size={15} />}
                    onClick={() =>
                      navigateTo(`/petty-cash-requisition/view/${record.id}`)
                    }
                  >
                    View
                  </Menu.Item>
                  {hasRole(getUserRole(), "custodian", "admin") && (
                    <Menu.Item
                      key="download-voucher"
                      icon={<FileText strokeWidth={1.5} size={15} />}
                      disabled={downloadingId === record.id}
                      onClick={() => createPettyCashVoucher(record)}
                    >
                      {downloadingId === record.id
                        ? "Downloading..."
                        : "Download Voucher"}
                    </Menu.Item>
                  )}
                </Menu>
              );
              return (
                <Dropdown overlay={menu} trigger={["click"]}>
                  <Button>
                    Actions <ChevronDown strokeWidth={0.5} />
                  </Button>
                </Dropdown>
              );
            },
          },
        ];
      case "cancelled":
        return [
          ...baseColumns,
          {
            title: "Cancelled By",
            dataIndex: "disapprover_name",
            key: "disapprover_name",
            render: (value: any, record: any) =>
              value || record.disapproved_by_name || record.cancelled_by_name || "-",
          },
          {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            render: (value: any, record: any) =>
              value || record.disapprove_reason || record.remarks || "-",
          },
          {
            title: "",
            key: "action",
            width: 10,
            align: "center" as const,
            render: (_text: any, record: any) => (
              <Button
                type="primary"
                size="small"
                onClick={() => navigateTo(`/petty-cash-requisition/view/${record.id}`)}
              >
                View
              </Button>
            ),
          },
        ];
      default:
        return [];
    }
  }

  function handleFilterChange(value: any, field: string) {
    if (field === "dates") {
      if (!value || value.length !== 2 || !value[0] || !value[1]) {
        setFilterConfig((prev: any) => ({
          ...prev,
          date_from: "",
          date_to: "",
          page: 1,
        }));
        return;
      }
      setFilterConfig((prev: any) => ({
        ...prev,
        date_from: value[0].format("YYYY-MM-DD"),
        date_to: value[1].format("YYYY-MM-DD"),
        page: 1,
      }));
      return;
    }

    setFilterConfig((prev: any) => ({
      ...prev,
      [field]: value ?? "",
      page: 1,
    }));
  }

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Petty Cash Requisition"
              withDateRangeFilter={false}
              withAddButton={hasRole(getUserRole(), "custodian")}
              addBtnText="Requisition"
              onCreate={() => navigateTo("/petty-cash-requisition/add")}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({
                  ...prev,
                  search: value ?? "",
                  page: 1,
                }))
              }
            />
          </Col>
          <Col xs={24} className="mb-1">
            <span>
              <strong>Filter By:</strong>
            </span>
          </Col>
          <Col xs={24} md={12} lg={8} className="mb-3">
            <RangePicker
              value={[
                filterConfig.date_from ? dayjs(filterConfig.date_from) : null,
                filterConfig.date_to ? dayjs(filterConfig.date_to) : null,
              ]}
              format="MMMM D, YYYY"
              onChange={(e) => handleFilterChange(e, "dates")}
              allowClear
              className="w-full"
            />
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <Tabs
                  type="card"
                  className="custom-tabs"
                  activeKey={activeTab}
                  onChange={onChangeTab}
                >
                  <TabPane tab="For Approval" key="pending">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                    />
                  </TabPane>
                  <TabPane tab="Approved" key="approved">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                    />
                  </TabPane>
                  <TabPane tab="Completed" key="completed">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                    />
                  </TabPane>
                  <TabPane tab="Cancelled" key="cancelled">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={{
                        current: filterConfig.page,
                        total: dataSize,
                        showSizeChanger: true,
                        onChange: (page: number) => handleChangePage(page),
                        onShowSizeChange: (_: number, size: number) =>
                          handlePageSizeChange(size),
                      }}
                    />
                  </TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>

      <PersistentFooter />
    </Layout>
  );
};

export default PettyCashRequisition;
